// @flow

import React, { useState } from 'react'
import _ from 'lodash'
import { injectIntl } from 'react-intl'
import { colors } from '@app/themes'
import { extractAddressFromResponse, maskAddress } from '@app/utils/addressMaskingUtils'
import { revealAddressApi } from '@app/services/api/revealAddress/revealAddressApi'
import { T } from '@nv/rc/Components'
import { AddressContainer, RevealButton } from './styles'
import { showNotification } from '@app/utils/notify'
import { NOTIFICATIONS } from '@app/utils/constants'

type Props = {
  address: string,
  orderId?: string,
  country?: string,
  addressField?: string,
  intl: any,
  'data-testid'?: string,
  externalRevealedAddress?: string,
  onRevealSuccess?: Function
}

/**
 * MaskedAddress component that displays masked addresses
 * with "Click to reveal (tracked)" functionality
 *
 * @param {string} address - The full address to mask
 * @param {string} orderId - The order/tracking ID for API call (optional)
 * @param {string} country - The country code (optional)
 * @param {string} addressField - Specific address field to extract (e.g., 'address1', 'address2') (optional)
 * @param {string} data-testid - Optional test ID
 * @param {string} externalRevealedAddress - Optional externally controlled revealed address
 * @param {Function} onRevealSuccess - Optional callback when address is successfully revealed
 */
const MaskedAddress = ({
  address,
  orderId,
  country,
  addressField,
  intl,
  'data-testid': testId,
  externalRevealedAddress,
  onRevealSuccess
}: Props) => {
  const [isRevealed, setIsRevealed] = useState(false)
  const [revealedAddress, setRevealedAddress] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const showRevealed = isRevealed || !_.isNil(externalRevealedAddress)
  const addressToDisplay = !_.isNil(externalRevealedAddress) ? externalRevealedAddress : revealedAddress

  const handleReveal = async () => {
    if (showRevealed || isLoading) return

    if (!orderId || !country) {
      setIsRevealed(true)
      setRevealedAddress(address)
      return
    }

    setIsLoading(true)
    setError(false)

    try {
      const response = await revealAddressApi(country, orderId)

      if (!response.ok || response.problem) {
        setError(true)
        const errorMessage = response?.data?.error?.message || intl.formatMessage({ id: 'unable_to_reveal_address' })
        showNotification(errorMessage, NOTIFICATIONS.ERROR, 5000)
        return
      }

      const fullAddress = extractAddressFromResponse(response, addressField)

      if (fullAddress !== null && fullAddress !== undefined) {
        setRevealedAddress(fullAddress)
        setIsRevealed(true)

        if (onRevealSuccess && response?.data?.data) {
          onRevealSuccess(response.data.data)
        }

        const warningMessage = intl.formatMessage({ id: 'contact_revealed_warning' })
        showNotification(warningMessage, NOTIFICATIONS.ERROR, 5000)
      } else {
        setError(true)
        const errorMessage = response?.data?.error?.message || intl.formatMessage({ id: 'unable_to_reveal_address' })
        showNotification(errorMessage, NOTIFICATIONS.ERROR, 5000)
      }
    } catch (err) {
      setError(true)
      const errorMessage = err?.data?.error?.message || intl.formatMessage({ id: 'unable_to_reveal_address' })
      showNotification(errorMessage, NOTIFICATIONS.ERROR, 5000)
    } finally {
      setIsLoading(false)
    }
  }

  const maskedAddress = maskAddress(address)

  if (!address && !addressToDisplay) return null

  if (showRevealed && !_.isNil(addressToDisplay)) {
    return (
      <AddressContainer data-testid={testId}>
        <span data-testid={`${testId}-revealed`}>{addressToDisplay}</span>
      </AddressContainer>
    )
  }

  if (error) {
    return (
      <AddressContainer data-testid={`${testId}-error`}>
        <span style={{ color: colors.salmon }}>
          <T id='unable_to_reveal_address' />
        </span>
        {maskedAddress && <span data-testid={`${testId}-masked`}> {maskedAddress}</span>}
      </AddressContainer>
    )
  }

  return (
    <AddressContainer data-testid={testId}>
      <RevealButton onClick={handleReveal} data-testid={`${testId}-reveal`}>
        {isLoading ? <T id='loading' /> : <T id={orderId && country ? 'click_to_reveal_tracked' : 'click_to_reveal'} />}
      </RevealButton>
      {maskedAddress && <span data-testid={`${testId}-masked`}>{maskedAddress}</span>}
    </AddressContainer>
  )
}

export default injectIntl(MaskedAddress)
